import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Crown, Lock, Sparkles } from "lucide-react";
import Header from "@/components/Header";

/**
 * Wraps a route/component that requires premium access.
 * Behavior:
 *  - Guests → redirect to /login?next=<current path>
 *  - Logged-in users without premium → show a full-screen upgrade card
 *  - Premium/trial-active/admin users → render children
 */
export default function PremiumGate({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (user === false || !user) nav(`/login?next=${encodeURIComponent(location.pathname)}`);
  }, [user, loading, nav, location.pathname]);

  if (loading || user === null) return null;
  if (user === false || !user) return null;

  const status = user.subscription_status;
  const hasPremium = user.is_premium;

  if (hasPremium) return children;

  // Blocked — show upgrade card
  const trialEnded = status === "expired";
  return (
    <>
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
        <div className="cp-card p-8 sm:p-12 text-center" data-testid="paywall-card">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: "linear-gradient(135deg, rgba(255,126,103,0.15), rgba(244,168,59,0.15))" }}>
            {trialEnded ? <Lock className="w-8 h-8" style={{ color: "#C2185B" }} /> : <Crown className="w-8 h-8" style={{ color: "#F4A83B" }} />}
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Sparkles className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /><span>Premium feature</span></div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24] mt-2">
            {trialEnded ? "Your 14 free days are up" : "This is a Premium feature"}
          </h1>
          <p className="mt-3 text-[#857163] max-w-md mx-auto">
            {trialEnded
              ? "Loved the tools? Subscribe to keep your Baker Map pin, marketplace shop, AI tools, business calculators, and marketing generators."
              : "Start your free 14-day trial to unlock the Baker Map, marketplace, AI baker tools, business tools, and marketing generators. No card required to try."}
          </p>
          <div className="mt-6">
            <Link to="/upgrade" className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold hover:opacity-90 transition" style={{ background: "linear-gradient(135deg, #FF7E67, #FF9E85)" }} data-testid="go-upgrade-btn">
              {trialEnded ? "Subscribe — $7/mo" : "Start free trial"}
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#857163]">14-day free trial · cancel anytime · $7/month after</p>
        </div>
      </main>
    </>
  );
}
